import {FormEvent, useEffect, useRef, useState} from 'react'
import {
  Camera,
  Check,
  CircleUserRound,
  Mail,
  ShieldCheck,
  Store,
  UserCheck,
  UserRound,
  X,
} from 'lucide-react'
import {sha256} from 'crypto-hash'

import * as API from '../../api'
import {getUserData, isSuperAdmin} from '../../modules/auth/core/Auth'
import CropperComponent from '../../pages/utils/ImageCropper'
import {messages} from './../utils/ErrorsText'

function initials(name = '') {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

const UserModal = (props: any) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [loading, setLoading] = useState(false)
  const [name, setName] = useState(props.userData?.name || '')
  const [email, setEmail] = useState(props.userData?.email || '')
  const [isAdmin, setIsAdmin] = useState(props.userData ? props.userData.is_admin : 0)
  const [isActived, setIsActived] = useState(props.userData ? props.userData.is_actived : 1)
  const [isMailling, setIsMailling] = useState(props.userData ? props.userData.is_mailling : 1)
  const [isViewer, setIsViewer] = useState(props.userData ? props.userData.is_viewer : 0)
  const [idSellerSuperAdmin, setIdSellerSuperAdmin] = useState(props.userData?.id_seller || '')
  const [status, setStatus] = useState('')
  const [domain, setDomain] = useState('')
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [croppedImage, setCroppedImage] = useState('')
  const [uploadResponse, setUploadResponse] = useState<any>()
  const [showCropper, setShowCropper] = useState(false)

  const isEditing = Boolean(props.userData)
  const superAdmin = isSuperAdmin()
  const isSeller = props.userData ? props.userData.is_seller : 1
  const idSeller = props.userData?.id_seller || ''
  const imageSrc = croppedImage || uploadResponse?.path || props.userData?.src || ''

  useEffect(() => {
    API.getCompanyDomain()
      .then((response) => setDomain(response.data.domain || ''))
      .catch(() => setDomain(''))
  }, [])

  function onImageSelect(event: React.ChangeEvent<HTMLInputElement>) {
    const selectedFile = event.target.files?.[0]
    if (!selectedFile) return
    setImageFile(selectedFile)
    setShowCropper(true)
  }

  function validateEmail() {
    if (!/^[\w-.+]+@([\w-]+\.)+[\w-]{2,4}$/.test(email)) return 'is-invalid'
    if (!domain.trim()) return 'is-valid'

    const emailDomain = email.substring(email.lastIndexOf('@') + 1).toLocaleLowerCase('pt-BR')
    const allowedDomains = domain
      .split(',')
      .map((item) => item.trim().toLocaleLowerCase('pt-BR'))
      .filter(Boolean)
    return allowedDomains.some(
      (allowedDomain) => emailDomain === allowedDomain || emailDomain.endsWith(`.${allowedDomain}`)
    )
      ? 'is-valid'
      : 'is-invalid'
  }

  const emailValidation = email ? validateEmail() : ''
  const nameValidation = name && name.trim().length < 5 ? 'is-invalid' : name ? 'is-valid' : ''
  const canSubmit = emailValidation === 'is-valid' && name.trim().length >= 5 && !loading

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setStatus('')

    if (emailValidation !== 'is-valid') {
      setStatus('Informe um e-mail válido e permitido para esta empresa.')
      return
    }
    if (name.trim().length < 5) {
      setStatus('O nome deve conter no mínimo 5 caracteres.')
      return
    }

    setLoading(true)
    try {
      if (isEditing) {
        const response: any = await API.updateUser({
          user_guid: props.userData.guid,
          name: name.trim(),
          email: email.trim(),
          password: '',
          id_upload: uploadResponse?.id_upload,
          is_actived: isActived,
          is_mailling: isMailling,
          is_viewer: isViewer,
          is_admin: isAdmin,
          company_guid: props.userData.company_guid,
          id_seller: superAdmin ? idSellerSuperAdmin : isSeller ? idSeller : undefined,
        })
        if (!response.success) {
          setStatus(messages[response.error] || 'Não foi possível atualizar o usuário.')
          return
        }
        props.onUserUpdated()
      } else {
        const companyGuid = superAdmin
          ? localStorage.getItem('current_company')
          : getUserData().company_guid
        const response: any = await API.createUser({
          name: name.trim(),
          email: email.trim(),
          password: await sha256(new Date().getTime().toString()),
          id_upload: uploadResponse?.id_upload,
          is_actived: isActived,
          is_mailling: isMailling,
          is_viewer: isViewer,
          is_admin: isAdmin,
          company_guid: companyGuid,
          id_seller: superAdmin ? idSellerSuperAdmin : isSeller ? idSeller : undefined,
        })
        if (!response.success) {
          setStatus(messages[response.error] || 'Não foi possível criar o usuário.')
          return
        }
        props.onUserCreated()
      }
    } catch {
      setStatus('Não foi possível salvar os dados. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form className='ua-user-modal' onSubmit={onSubmit}>
      <header className='ua-user-modal__header'>
        <span className='ua-user-modal__header-icon'>
          {isEditing ? <UserCheck size={22} /> : <CircleUserRound size={22} />}
        </span>
        <div>
          <span>{isEditing ? 'Atualização de cadastro' : 'Novo acesso'}</span>
          <h2>{isEditing ? 'Editar usuário' : 'Criar usuário'}</h2>
          <p>
            {isEditing
              ? 'Atualize os dados e preferências desta pessoa.'
              : 'Cadastre uma nova pessoa e defina suas preferências iniciais.'}
          </p>
        </div>
        <button
          type='button'
          className='ua-user-modal__close'
          aria-label='Fechar modal'
          onClick={() => props.onClose()}
        >
          <X size={18} />
        </button>
      </header>

      <div className='ua-user-modal__body'>
        <section className='ua-user-modal__profile'>
          <div className='ua-user-modal__photo'>
            <div className='ua-user-modal__avatar'>
              {imageSrc ? (
                <img src={imageSrc} alt={`Foto de ${name || 'usuário'}`} />
              ) : (
                initials(name) || <UserRound size={34} />
              )}
            </div>
            <input ref={inputRef} type='file' accept='image/*' hidden onChange={onImageSelect} />
            <button
              type='button'
              className='ua-secondary-button'
              onClick={() => inputRef.current?.click()}
            >
              <Camera size={15} /> {imageSrc ? 'Trocar foto' : 'Adicionar foto'}
            </button>
            <small>JPG ou PNG, com enquadramento quadrado.</small>
          </div>

          <div className='ua-user-modal__fields'>
            {domain && (
              <div className='ua-user-modal__domain'>
                <ShieldCheck size={16} />
                <span>
                  E-mails permitidos: <strong>{domain}</strong>
                </span>
              </div>
            )}

            <div className='ua-user-modal__field-grid'>
              <label className='ua-user-modal__field'>
                <span>
                  <Mail size={14} /> E-mail
                </span>
                <input
                  type='email'
                  value={email}
                  className={emailValidation}
                  placeholder='nome@empresa.com.br'
                  autoComplete='email'
                  onChange={(event) => setEmail(event.target.value)}
                />
                {emailValidation === 'is-invalid' && email && (
                  <small>Confira o formato e o domínio informado.</small>
                )}
              </label>

              <label className='ua-user-modal__field'>
                <span>
                  <UserRound size={14} /> Nome completo
                </span>
                <input
                  type='text'
                  value={name}
                  className={nameValidation}
                  placeholder='Nome e sobrenome'
                  autoComplete='name'
                  onChange={(event) => setName(event.target.value)}
                />
                {nameValidation === 'is-invalid' && <small>Use pelo menos 5 caracteres.</small>}
              </label>

              {superAdmin && (
                <label className='ua-user-modal__field'>
                  <span>
                    <Store size={14} /> ID do vendedor
                  </span>
                  <input
                    type='text'
                    value={idSellerSuperAdmin}
                    placeholder='ID externo do vendedor'
                    onChange={(event) => setIdSellerSuperAdmin(event.target.value)}
                  />
                </label>
              )}

              {superAdmin && (
                <label className='ua-user-modal__field'>
                  <span>
                    <ShieldCheck size={14} /> Perfil de acesso
                  </span>
                  <select
                    value={isAdmin}
                    onChange={(event) => setIsAdmin(event.target.value === '1' ? 1 : 0)}
                  >
                    <option value='0'>Usuário</option>
                    <option value='1'>Administrador</option>
                  </select>
                </label>
              )}
            </div>
          </div>
        </section>

        {showCropper && imageFile && (
          <section className='ua-user-modal__cropper'>
            <div>
              <h3>Ajustar foto de perfil</h3>
              <p>Reposicione e corte a imagem antes de confirmar.</p>
            </div>
            <CropperComponent
              imageFile={imageFile}
              setCroppedImage={setCroppedImage}
              setShowCropper={setShowCropper}
              setUploadResponse={setUploadResponse}
            />
          </section>
        )}

        <section className='ua-user-modal__access'>
          <div className='ua-user-modal__section-title'>
            <div>
              <h3>Preferências de acesso</h3>
              <p>Defina a disponibilidade do cadastro e os canais permitidos.</p>
            </div>
          </div>

          <div className='ua-user-modal__settings'>
            <div className='ua-user-modal__setting'>
              <div>
                <span className='ua-user-modal__setting-icon'>
                  <UserCheck size={17} />
                </span>
                <span>
                  <strong>Status do acesso</strong>
                  <small>Controla se a pessoa consegue entrar na plataforma.</small>
                </span>
              </div>
              <div className='ua-segmented-control'>
                <label className={isActived === 1 ? 'is-active' : ''}>
                  <input
                    type='radio'
                    name='status'
                    checked={isActived === 1}
                    onChange={() => setIsActived(1)}
                  />
                  <Check size={13} /> Ativo
                </label>
                <label className={isActived === 0 ? 'is-active is-negative' : ''}>
                  <input
                    type='radio'
                    name='status'
                    checked={isActived === 0}
                    onChange={() => setIsActived(0)}
                  />
                  Inativo
                </label>
              </div>
            </div>

            <div className='ua-user-modal__setting'>
              <div>
                <span className='ua-user-modal__setting-icon'>
                  <Mail size={17} />
                </span>
                <span>
                  <strong>Receber mailing</strong>
                  <small>Autoriza comunicações e informativos por e-mail.</small>
                </span>
              </div>
              <div className='ua-segmented-control'>
                <label className={isMailling === 1 ? 'is-active' : ''}>
                  <input
                    type='radio'
                    name='mailing'
                    checked={isMailling === 1}
                    onChange={() => setIsMailling(1)}
                  />
                  Sim
                </label>
                <label className={isMailling === 0 ? 'is-active' : ''}>
                  <input
                    type='radio'
                    name='mailing'
                    checked={isMailling === 0}
                    onChange={() => setIsMailling(0)}
                  />
                  Não
                </label>
              </div>
            </div>

            {superAdmin && (
              <div className='ua-user-modal__setting'>
                <div>
                  <span className='ua-user-modal__setting-icon'>
                    <ShieldCheck size={17} />
                  </span>
                  <span>
                    <strong>Perfil visualizador</strong>
                    <small>Restringe o cadastro a experiências somente de leitura.</small>
                  </span>
                </div>
                <div className='ua-segmented-control'>
                  <label className={isViewer === 1 ? 'is-active' : ''}>
                    <input
                      type='radio'
                      name='viewer'
                      checked={isViewer === 1}
                      onChange={() => setIsViewer(1)}
                    />
                    Sim
                  </label>
                  <label className={isViewer === 0 ? 'is-active' : ''}>
                    <input
                      type='radio'
                      name='viewer'
                      checked={isViewer === 0}
                      onChange={() => setIsViewer(0)}
                    />
                    Não
                  </label>
                </div>
              </div>
            )}
          </div>
        </section>

        {status && <div className='ua-feedback ua-feedback--danger'>{status}</div>}
      </div>

      <footer className='ua-user-modal__footer'>
        <span>Os campos de nome e e-mail são obrigatórios.</span>
        <div>
          <button type='button' className='ua-secondary-button' onClick={() => props.onClose()}>
            Cancelar
          </button>
          <button type='submit' className='ua-primary-button' disabled={!canSubmit}>
            {loading ? <span className='ua-spinner' /> : <Check size={15} />}
            {loading ? 'Salvando…' : isEditing ? 'Salvar alterações' : 'Criar usuário'}
          </button>
        </div>
      </footer>
    </form>
  )
}

export default UserModal
